﻿import React, { useEffect, useRef, useState } from 'react';

import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { ButtonGroup, Dropdown, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { Variant } from 'react-bootstrap/esm/types';
import { useStableId } from 'hooks';

import 'components/GMetrixDropdown/gmetrix-dropdown.scss';
interface GMetrixDropdownProps<T> {
    className?: string;
    customDisplayText?: any;
    dataItemKey?: Extract<keyof T, string>;
    dataItems: T[];
    disabled?: boolean;
    filterable?: boolean;
    iconClass?: string;
    infoTitle?: string;
    isValid?: boolean;
    label?: string;
    limitMenuWidth?: boolean;
    maxWidth?: number;
    menuOffsetWidth?: number
    multiSelect?: boolean;
    onBlur?: (data: T | T[]) => void;
    onChange: (value: T | T[], index?: number) => void;
    placeholder?: string;
    required?: boolean;
    style?: React.CSSProperties;
    subLabel?: string;
    tabIndex?: number;
    textField?: Extract<keyof T, string>;
    value?: T | T[];
    variant?: Variant;
}

const MIN_TOGGLE_HEIGHT = 38; // keep a stable control height even when empty or rendering objects

export function GMetrixDropdown<T>({
    className,
    customDisplayText,
    dataItemKey = 'id' as Extract<keyof T, string>,
    dataItems = [],
    disabled = false,
    filterable = true,
    iconClass,
    infoTitle,
    isValid = true,
    label,
    limitMenuWidth = false,
    maxWidth,
    menuOffsetWidth,
    multiSelect = false,
    onBlur,
    onChange,
    placeholder,
    required,
    style,
    subLabel,
    tabIndex,
    textField = 'text' as Extract<keyof T, string>,
    value,
    variant = 'outline-secondary'
}: GMetrixDropdownProps<T>) {

    const { formatMessage } = useIntl();

    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const toggleId = useStableId('gmx-dropdown');
    const menuId = `${toggleId}-menu`;

    const [open, setOpen] = useState(false);
    const hasBeenOpened = useRef(false);
    const pendingSelectedRef = useRef<T[]>([]);
    const [pendingSelected, setPendingSelected] = useState<T[]>([]);

    useEffect(() => {

        if (open) {

            hasBeenOpened.current = true;
            if (multiSelect) {

                const initial = Array.isArray(value) ? [...value] : value ? [value as T] : [];
                pendingSelectedRef.current = initial;
                setPendingSelected(initial);

            }

        } else if (hasBeenOpened.current) {

            if (multiSelect) {

                onChange(pendingSelectedRef.current);
                onBlur?.(pendingSelectedRef.current);

            } else {

                onBlur?.(Array.isArray(value) ? value : value ? [value] : [] as any);

            }

        }

    }, [open]);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuWidth, setMenuWidth] = useState('auto');
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

    const filteredItems = dataItems.filter(item => !filterable || !searchTerm
        ? true
        : String(item[textField]).toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {

        if (toggleRef.current) {

            setMenuWidth(toggleRef.current.offsetWidth + (menuOffsetWidth ?? 0) as any);

        }

    }, []);

    /** Close on outside click */
    useEffect(() => {

        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {

            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                !toggleRef.current?.contains(e.target as Node)
            ) {

                setOpen(false);
                setFocusedIndex(-1);

            }

        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);

    }, [open]);

    const isSelected = (item: T) => multiSelect
        ? pendingSelected.some(selected => selected[dataItemKey] === item[dataItemKey])
        : selectedValues.some(selected => selected[dataItemKey] === item[dataItemKey]);

    const toggleItem = (item: T) => {

        if (!multiSelect) {

            onChange(item);
            setOpen(false);
            return;

        }

        const exists = pendingSelectedRef.current.some(selected => selected[dataItemKey] === item[dataItemKey]);
        const newValues = exists
            ? pendingSelectedRef.current.filter(selected => selected[dataItemKey] !== item[dataItemKey])
            : [...pendingSelectedRef.current, item];
        pendingSelectedRef.current = newValues;
        setPendingSelected(newValues);

    };

    const selectAll = () => {

        if (multiSelect) {

            pendingSelectedRef.current = [...filteredItems];
            setPendingSelected([...filteredItems]);

        } else {

            onChange(filteredItems);

        }

    };

    const clearAll = () => {

        if (multiSelect) {

            pendingSelectedRef.current = [];
            setPendingSelected([]);

        } else {

            onChange([]);

        }

    };

    const handleKeyDown = (e: React.KeyboardEvent) => {

        // Allow space in search input
        if ((e.target as HTMLElement)?.tagName === 'INPUT') {

            return;

        }

        if (!open) return;

        switch (e.key) {

            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(i => Math.min(i + 1, filteredItems.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(i => Math.max(i - 1, 0));
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (focusedIndex >= 0) {

                    toggleItem(filteredItems[focusedIndex]);

                }
                break;
            case 'Escape':
                setOpen(false);
                setFocusedIndex(-1);
                break;

        }

    };

    const displayText = () => {

        if (customDisplayText) {

            return customDisplayText;

        }
        if (multiSelect) {

            if (pendingSelected.length === 0) {

                return placeholder ?? formatMessage({ id: 'select.ea1021d6', defaultMessage: 'Select...' });

            }
            return formatMessage({ id: 'count_of_total_selected.090f44ac', defaultMessage: '{count} of {total} selected' }, { count: pendingSelected.length, total: dataItems.length });

        }

        const hasValue = value && !Array.isArray(value);
        const label = hasValue ? (value as any)[textField] : undefined;
        return label && String(label).trim().length > 0
            ? String(label)
            : (placeholder ?? formatMessage({ id: 'select.ea1021d6', defaultMessage: 'Select...' }));

    };

    return (
        <div className={`${className ?? ''} d-flex flex-column w-100`} style={style}>
            {label && (
                <div className="d-flex align-items-center">
                    <Form.Label className="fw-bold" htmlFor={toggleId}>
                        {label}
                        {required && '*'}
                    </Form.Label>
                    {subLabel && (
                        <small className="text-muted ms-1">
                            {subLabel}
                        </small>
                    )}
                    {infoTitle && <ToolTip className="mb-1" content={infoTitle} />}
                    {disabled && <i className="p-1 ms-1 fa-solid fa-lock" />}
                </div>
            )}

            <Dropdown as={ButtonGroup} show={open} style={{ maxWidth: '100%' }} tabIndex={tabIndex}>
                <Dropdown.Toggle
                    aria-controls={menuId}
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    aria-label={label ? undefined : placeholder}
                    className={`d-flex justify-content-between align-items-center fw-normal ${isValid ? '' : 'dropdown-not-valid'}`}
                    disabled={disabled}
                    id={toggleId}
                    onClick={() => setOpen(o => !o)}
                    placeholder={placeholder}
                    ref={toggleRef}
                    style={{ lineHeight: '1.2', maxWidth, minHeight: MIN_TOGGLE_HEIGHT }}
                    variant={variant}
                >
                    {iconClass && <i className={`me-2 ${iconClass}`} />}
                    <span className="text-truncate">
                        {displayText()}
                    </span>
                </Dropdown.Toggle>

                <Dropdown.Menu
                    renderOnMount
                    popperConfig={{
                        modifiers: [
                            {
                                name: 'computeStyles',
                                options: {
                                    gpuAcceleration: false
                                }
                            }
                        ],
                        strategy: 'fixed'
                    }}
                    aria-multiselectable={multiSelect}
                    id={menuId}
                    onKeyDown={handleKeyDown}
                    ref={menuRef}
                    role="listbox"
                    style={{ maxWidth: limitMenuWidth ? '100%' : menuWidth }}
                >
                    {filterable && (
                        <div className="px-2 pb-2">
                            <GMetrixInput
                                autoFocus
                                onChange={e => {

                                    setSearchTerm(e.target.value);
                                    setFocusedIndex(0);

                                }}
                                value={searchTerm}
                            />
                        </div>
                    )}

                    {multiSelect && (
                        <Dropdown.Item
                            onClick={e => {

                                e.preventDefault();
                                if (pendingSelected.length === dataItems.length) {

                                    clearAll();

                                } else {

                                    selectAll();

                                }

                            }}
                            className="d-flex align-items-center"
                            key="selectAll"
                        >
                            {multiSelect && (
                                <Form.Check
                                    readOnly
                                    checked={pendingSelected.length === dataItems.length}
                                    className="me-2"
                                    style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                    type="checkbox"
                                />
                            )}
                            <FormattedMessage defaultMessage="Select All" id="select_all.dd487b25" />
                        </Dropdown.Item>
                    )}

                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {filteredItems.map((item, index) => (
                            <Dropdown.Item
                                onClick={e => {

                                    e.preventDefault();
                                    toggleItem(item);

                                }}
                                active={focusedIndex === index}
                                aria-selected={isSelected(item)}
                                className="d-flex align-items-center"
                                key={String(item[dataItemKey])}
                                onMouseEnter={() => setFocusedIndex(index)}
                                role="option"
                            >
                                {multiSelect && (
                                    <Form.Check
                                        readOnly
                                        checked={isSelected(item)}
                                        className="me-2"
                                        style={{ pointerEvents: 'none' }}
                                        type="checkbox"
                                    />
                                )}
                                {String(item[textField])}
                            </Dropdown.Item>
                        ))}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );

}
